import React, { useState } from 'react';
import Dialog from './Dialog';

interface ImageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, alt: string, caption?: string) => void;
}

const ImageDialog: React.FC<ImageDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [url, setUrl] = useState('');
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      setIsValidUrl(true);
    } catch {
      setIsValidUrl(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    validateUrl(newUrl);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidUrl) {
      onInsert(url, alt, caption);
      onClose();
      // Reset form
      setUrl('');
      setAlt('');
      setCaption('');
      setIsValidUrl(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Insert Image">
      <form onSubmit={handleSubmit} className="mj-image-dialog-form">
        <div className="mj-form-group">
          <label htmlFor="image-url">Image URL: *</label>
          <input
            id="image-url"
            type="url"
            value={url}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg"
            className={`mj-form-input ${url && !isValidUrl ? 'mj-form-input-error' : ''}`}
            required
          />
          {url && !isValidUrl && (
            <div className="mj-form-error">Please enter a valid URL</div>
          )}
        </div>
        
        <div className="mj-form-group">
          <label htmlFor="image-alt">Alt Text:</label>
          <input
            id="image-alt"
            type="text"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            placeholder="Description of the image"
            className="mj-form-input"
          />
        </div>

        <div className="mj-form-group">
          <label htmlFor="image-caption">Caption:</label>
          <input
            id="image-caption"
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Optional caption for the image"
            className="mj-form-input"
          />
        </div>

        {isValidUrl && (
          <div className="mj-dialog-preview">
            <h4>Preview:</h4>
            <div className="mj-image-preview">
              <img 
                src={url} 
                alt={alt || 'Image preview'} 
                className="mj-preview-image"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('mj-hidden');
                }}
              />
              <div className="mj-image-error mj-hidden">
                Unable to load image preview
              </div>
            </div>
          </div>
        )}

        <div className="mj-dialog-actions">
          <button type="button" onClick={onClose} className="mj-btn mj-btn-secondary">
            Cancel
          </button>
          <button 
            type="submit" 
            className="mj-btn mj-btn-primary"
            disabled={!isValidUrl}
          >
            Insert Image
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default ImageDialog; 